/**
 * Running inside the Shopify admin.
 *
 * The dashboard has two homes. Normally it is a first-party app at
 * app.outfalakcheckout.com, signed in with a session cookie. When a merchant opens
 * Outfalak Checkout from Shopify → Apps, the *same build* is rendered in an iframe
 * inside admin.shopify.com — and almost nothing about authentication survives
 * that move:
 *
 *   • The session cookie is third-party there. Safari and Firefox drop it
 *     outright and Chrome increasingly does, so there is no cookie to read.
 *   • There is no user row to sign in as. A Shopify merchant never registered
 *     with us; Shopify vouching for them *is* the credential.
 *
 * What replaces both is App Bridge's session token: a short-lived JWT, signed by
 * the app's own client secret, naming the store. The API already understands it
 * (`resolvePrincipal` accepts it as a bearer), so the client's whole job is to
 * fetch a fresh one per request.
 *
 * ── Why App Bridge is injected rather than put in index.html ────────────────
 *
 * Shopify documents the script as the first tag in `<head>`. That would load it
 * on every standalone dashboard page too — for merchants who are not in the
 * admin at all, where it has no context to attach to. Injecting it only when the
 * page really is embedded means the standalone dashboard cannot be affected by
 * it at all, which is the one property worth protecting here: a broken embedded
 * app is a feature not working, a broken standalone dashboard is every merchant
 * locked out.
 */

/**
 * Stashed because client-side routing drops the query string Shopify sent.
 *
 * Both matter, and `shop` matters more than it looks: it decides which app's
 * client id App Bridge is loaded under. Read fresh from the URL on every call,
 * it is present on the first render and gone the moment the merchant clicks
 * anything — after which the resolver falls back to the *platform* app, App
 * Bridge mints tokens for the wrong one, and every request 401s with nothing to
 * say why.
 */
const HOST_KEY = 'outfalak.shopify.host'
const SHOP_KEY = 'outfalak.shopify.shop'

declare global {
  interface Window {
    shopify?: { idToken: () => Promise<string> }
  }
}

/**
 * Where the App Bridge client id comes from, now that it is not resolved here.
 *
 * It used to be: this module asked `/v1/shopify/app?shop=` at runtime, so one
 * bundle could serve several Shopify apps — the platform's, and a client id per
 * merchant for custom-distributed ones. That was the right idea and it cannot
 * survive where the tag has to live. Shopify requires App Bridge to be the first
 * script in the document, which means the key must be known before any of this
 * code has run, let alone before it could ask.
 *
 * So the key is written into index.html at build time from
 * `VITE_SHOPIFY_API_KEY`, and the runtime lookup is gone rather than left here
 * pretending to do something.
 *
 * The cost is real and is stated where it bites: a deployment serving more than
 * one Shopify app needs its index.html served by the API, which can resolve the
 * key per shop, instead of as a static file from nginx. `/v1/shopify/app` still
 * exists and still answers — it is simply no longer this file's caller.
 */


/**
 * Is this page running inside the Shopify admin?
 *
 * Read once, from the URL Shopify actually navigated to, and remembered for the
 * tab. The first render is the only one that carries `?host=` — every route
 * change after it is `history.pushState`, which drops the query string, and a
 * check that re-read the URL each time would decide the app had stopped being
 * embedded the moment the merchant clicked anything.
 *
 * `sessionStorage`, not `localStorage`: the flag must not outlive the tab, or a
 * merchant who later opens the standalone dashboard in the same browser would
 * be told they are embedded and get no sign-in screen.
 */
let embedded: boolean | null = null

export function isEmbedded(): boolean {
  if (embedded !== null) return embedded

  const params = new URLSearchParams(window.location.search)
  const host = params.get('host')

  remember(SHOP_KEY, params.get('shop'))

  if (host) {
    try {
      sessionStorage.setItem(HOST_KEY, host)
    } catch {
      // Private browsing with storage denied. The flag below still holds for
      // this page load, which is all the first connect actually needs.
    }
  }

  let remembered: string | null = null
  try {
    remembered = sessionStorage.getItem(HOST_KEY)
  } catch {
    remembered = null
  }

  embedded = Boolean(host ?? remembered) || params.get('embedded') === '1'

  return embedded
}

export function shopifyHost(): string | null {
  return recall(HOST_KEY)
}

/** The store this embedded session belongs to, however far the merchant has navigated. */
export function shopifyShop(): string | null {
  return recall(SHOP_KEY)
}

function remember(key: string, value: string | null): void {
  if (!value) return
  try {
    sessionStorage.setItem(key, value)
  } catch {
    // Private browsing with storage denied. The first page load still works;
    // only navigation within the session loses the value.
  }
}

function recall(key: string): string | null {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

/**
 * `id_token` from the URL — the session token Shopify puts on the first load.
 *
 * The fallback for the moment before App Bridge has loaded, and the reason the
 * very first `/v1/shopify/connect` can be made without waiting for it. It lasts
 * about a minute, so it is never cached.
 */
export function idTokenFromUrl(): string | null {
  return new URLSearchParams(window.location.search).get('id_token')
}

let bridge: Promise<void> | null = null

/** Injects App Bridge once and resolves when `window.shopify` is usable. */
export function ensureAppBridge(): Promise<void> {
  if (bridge) return bridge

  bridge = (async () => {
    // index.html writes the tag while the parser is still running, so by the
    // time any module here executes App Bridge has already run. This waits for
    // it rather than loading it, because loading it late is precisely what does
    // not work — see the comment on that tag.
    if (window.shopify) return

    /*
     * Not there. Either the page is not embedded — in which case nothing should
     * be asking — or the CDN did not answer. A short poll rather than an
     * immediate throw: `defer`red modules can, on some engines, run before a
     * classic script written into the head has finished fetching.
     */
    for (let waited = 0; waited < 3000; waited += 50) {
      await new Promise((resolve) => setTimeout(resolve, 50))
      if (window.shopify) return
    }

    throw new Error('Shopify App Bridge did not load.')
  })()

  return bridge
}

/**
 * An embedded page that cannot prove which store it is.
 *
 * Its own class because the caller has to be able to tell it apart from every
 * other reason a request did not happen. This one must never be retried on
 * ambient credentials — that is the whole point of it existing.
 */
export class EmbeddedSessionError extends Error {
  constructor() {
    super('This app could not get a session token from Shopify.')
    this.name = 'EmbeddedSessionError'
  }
}

/**
 * A session token good for the next request, and not one moment longer.
 *
 * Deliberately never cached. Shopify issues these with a sixty-second life, and
 * a token held across a merchant leaving the tab open is a token that has
 * expired — App Bridge mints a fresh one far more cheaply than the request it
 * would fail.
 *
 * Falls back to the `id_token` Shopify put in the URL when App Bridge has not
 * loaded yet, which is what makes the very first call on a cold open work.
 *
 * ── Why this throws instead of returning null ───────────────────────────────
 *
 * It used to fail open: an embedded page where App Bridge never appeared got
 * `null`, and `api()` then sent the request anyway with `credentials:'include'`
 * and no `Authorization` header. The session cookie is `SameSite=None` on the
 * parent domain, so the browser *does* send it from inside the admin iframe,
 * and the API resolves the request as whoever is signed in to the standalone
 * dashboard in that browser. The merchant is told nothing and shown another
 * account's orders, wallet and stores inside their own Shopify admin.
 *
 * The ways it can happen are ordinary: a client id that does not match the app
 * the iframe belongs to (a custom-distributed merchant on a build carrying the
 * platform key), cdn.shopify.com blocked by an ad blocker or a corporate proxy,
 * an empty key shipped by a misconfigured build. None of them announce
 * themselves, and every one of them ends in the wrong tenant's data.
 *
 * So an embedded request now either carries a bearer or does not happen. A
 * visible error on the merchant's own screen is the strictly better failure.
 */
export async function sessionToken(): Promise<string | null> {
  if (!isEmbedded()) return null

  try {
    await ensureAppBridge()
    if (window.shopify) return await window.shopify.idToken()
  } catch {
    // App Bridge is not usable. The URL token, where there is one, is the same
    // claim signed by the same Shopify for the same store, so it may still
    // carry this one request.
  }

  const fromUrl = idTokenFromUrl()
  if (fromUrl) return fromUrl

  throw new EmbeddedSessionError()
}

/**
 * Send the merchant to a URL that must not be framed.
 *
 * Shopify's own screens — the app-charge approval in particular — refuse to
 * render inside an iframe, so navigating the embedded app to one shows the
 * merchant an empty box with no error. The escape is `_top`: App Bridge
 * intercepts it and performs a real top-level navigation out of the admin's
 * frame, which is the only way an embedded app can hand control to Shopify.
 *
 * Outside the admin there is no frame to break out of, so it is an ordinary
 * navigation — the same call works on the standalone dashboard.
 */
export function openTopLevel(url: string): void {
  if (!isEmbedded()) {
    window.location.href = url
    return
  }

  // `window.top.location` would be the obvious way and is blocked: the admin is
  // a different origin, so reading or writing its location throws. Only App
  // Bridge can do this, and `_top` is how it is asked.
  window.open(url, '_top')
}
